/**
 * Patient-scoped Care query keys.
 *
 * Patient identity is deliberately placed immediately after each domain root.
 * That gives mutations a stable prefix which can cancel, snapshot, update, and
 * invalidate one patient's queries without touching another patient's cache.
 */

export type AppointmentListKeyParams = {
  tenantId: string;
  patientId: string;
  startDate?: string;
  endDate?: string;
  date?: string;
  sort?: "asc" | "desc";
  sortBy?: "startDate" | "startTime" | "title" | "createdAt";
};

export const appointmentKeys = {
  all: ["appointments"] as const,
  patient: (tenantId: string, patientId: string) =>
    [...appointmentKeys.all, tenantId, patientId] as const,
  list: ({
    tenantId,
    patientId,
    startDate,
    endDate,
    date,
    sort,
    sortBy,
  }: AppointmentListKeyParams) =>
    [
      ...appointmentKeys.patient(tenantId, patientId),
      "list",
      { startDate, endDate, date, sort, sortBy },
    ] as const,
};

export const medicationLogKeys = {
  all: ["medicationLogs"] as const,
  patient: (tenantId: string, patientId: string) =>
    [...medicationLogKeys.all, tenantId, patientId] as const,
  list: (
    tenantId: string,
    patientId: string,
    startDate?: string,
    endDate?: string,
    limit?: number,
    timezone?: string,
  ) => {
    const filters: {
      startDate?: string;
      endDate?: string;
      limit?: number;
      timezone?: string;
    } = { startDate, endDate, limit };

    if (timezone !== undefined) filters.timezone = timezone;

    return [
      ...medicationLogKeys.patient(tenantId, patientId),
      "list",
      filters,
    ] as const;
  },
};

export const medicationDoseLogKeys = {
  all: ["medicationDoseLogs"] as const,
  patient: (tenantId: string, patientId: string) =>
    [...medicationDoseLogKeys.all, tenantId, patientId] as const,
  list: (
    tenantId: string,
    patientId: string,
    startDate?: string,
    endDate?: string,
    timezone?: string,
  ) => {
    const filters: {
      startDate?: string;
      endDate?: string;
      timezone?: string;
    } = { startDate, endDate };

    if (timezone !== undefined) filters.timezone = timezone;

    return [
      ...medicationDoseLogKeys.patient(tenantId, patientId),
      "list",
      filters,
    ] as const;
  },
};

export const calendarKeys = {
  all: ["calendar"] as const,
  patient: (tenantId: string, patientId: string) =>
    [...calendarKeys.all, tenantId, patientId] as const,
  medications: (tenantId: string, patientId: string) =>
    [...calendarKeys.patient(tenantId, patientId), "medications"] as const,
  appointments: (
    tenantId: string,
    patientId: string,
    startDate?: string,
    endDate?: string,
  ) =>
    [
      ...calendarKeys.patient(tenantId, patientId),
      "appointments",
      { startDate, endDate },
    ] as const,
  symptoms: (
    tenantId: string,
    patientId: string,
    startDate?: string,
    endDate?: string,
  ) =>
    [
      ...calendarKeys.patient(tenantId, patientId),
      "symptoms",
      { startDate, endDate },
    ] as const,
  /**
   * The patient's whole period log, unwindowed.
   *
   * No date range in the key: cycle statistics are derived from the full
   * history, so a windowed cache would answer a different question than the
   * one the fertile-window model asks. Nested under `patient`, which is what
   * `useLogPeriod` and `useDeletePeriodDayByDate` already invalidate — logging
   * a day therefore refreshes the prediction with no extra wiring.
   */
  periods: (tenantId: string, patientId: string) =>
    [...calendarKeys.patient(tenantId, patientId), "periods"] as const,
  calendarData: (
    tenantId: string,
    patientId: string,
    startDate: string,
    endDate: string,
  ) =>
    [
      ...calendarKeys.patient(tenantId, patientId),
      "data",
      { startDate, endDate },
    ] as const,
};

export const symptomKeys = {
  all: ["symptoms"] as const,
  patient: (tenantId: string, patientId: string) =>
    [...symptomKeys.all, tenantId, patientId] as const,
  list: (
    tenantId: string,
    patientId: string,
    startDate?: string,
    endDate?: string,
    limit?: number,
  ) =>
    [
      ...symptomKeys.patient(tenantId, patientId),
      "list",
      { startDate, endDate, limit },
    ] as const,
};

export const patientMedicationKeys = {
  all: ["patientMedications"] as const,
  patient: (tenantId: string, patientId: string) =>
    [...patientMedicationKeys.all, tenantId, patientId] as const,
  list: (tenantId: string, patientId: string) =>
    [...patientMedicationKeys.patient(tenantId, patientId), "list"] as const,
};

export const patientNoteKeys = {
  all: ["patientNotes"] as const,
  patient: (tenantId: string, patientId: string) =>
    [...patientNoteKeys.all, tenantId, patientId] as const,
  list: (tenantId: string, patientId: string) =>
    [...patientNoteKeys.patient(tenantId, patientId), "list"] as const,
};
